import React, { useState } from 'react';
import { MessageSquare, X, ChevronRight } from 'lucide-react';
import Button from '../../../ui/Button';
import Select from '../../../ui/Select';

interface MessagesCardProps {
  onClose?: () => void;
  onSave?: (messagesData: MessagesData) => void;
}

interface MessagesData {
  subscriptionPreferences: string;
  countTowardsLimit: boolean;
  messageLimit?: {
    enabled: boolean;
    limit: number;
    period: string;
  };
}

const MessagesCard: React.FC<MessagesCardProps> = ({ onClose, onSave }) => {
  const [messagesData, setMessagesData] = useState<MessagesData>({
    subscriptionPreferences: 'All subscribed people',
    countTowardsLimit: false,
    messageLimit: {
      enabled: false,
      limit: 5,
      period: 'month',
    },
  });

  const handleSave = () => {
    onSave?.(messagesData);
    onClose?.();
  };

  const subscriptionOptions = [
    { value: 'All subscribed people', label: 'All subscribed people' },
    { value: 'Only new subscribers', label: 'Only new subscribers' },
    { value: 'Custom segment', label: 'Custom segment' },
  ];

  return (
    <div className={`block `}>
      <div className="bg-white w-[32vw] border-l-2 border-grey-500 ">
        {/* Header */}
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 bg-success-background rounded-lg flex items-center justify-center">
              <MessageSquare className="w-4 h-4 text-success" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                Messages
              </h2>
              <p className="text-xs text-gray-600">
                Set the default settings for messages in this workflow. You can
                override them for each message.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-6">
          {/* Subscription Preferences */}
          <div className="space-y-2">
            <h3 className="font-semibold text-gray-900 text-sm">
              Subscription preferences
            </h3>
            <Select
              options={subscriptionOptions}
              value={messagesData.subscriptionPreferences}
              onChange={(e) =>
                setMessagesData((prev) => ({
                  ...prev,
                  subscriptionPreferences: e.target.value,
                }))
              }
              className="h-10 text-sm"
            />
            <p className="text-xs text-gray-600">
              Choose which people will receive messages from the campaign.
            </p>
          </div>

          {/* Count Towards Message Limit */}
          <div className="space-y-2">
            <h3 className="font-semibold text-gray-900 text-sm">
              Count towards message limit
            </h3>
            <p className="text-xs text-gray-600">
              Control how many messages a person receives in a given time period
              with a message limit.
            </p>
            <Button
              variant="outline"
              size="small"
              className="w-full flex items-center justify-center space-x-1 text-xs h-8"
            >
              <span>Set up message limit</span>
              <ChevronRight className="w-3 h-3" />
            </Button>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-4 gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            size="small"
            className="text-sm"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            size="small"
            className="text-sm"
          >
            Save changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MessagesCard;
